import { useEffect, useRef, type ComponentType } from 'react';
import { massiveColumnConfig } from '@/app/KeywordResearch/config/massiveColumnConfig';
import { Card, Col, Row } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetMassiveHistoryQuery } from '@/app/KeywordResearch/api/massiveApi';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import MassiveNewForm from '../components/keyword-massive/MassiveNewForm';
import MassiveFormSkeleton from '../components/keyword-massive/MassiveFormSkeleton';
import RelatedKeywordsModeToggle from '../components/keyword-related/RelatedKeywordsModeToggle';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import useDomain from '@/hooks/useDomain';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type AgGridWrapperProps = {
  settings?: Record<string, unknown>;
  height: string;
  columns: unknown[];
  data?: unknown[] | null;
};

type AgGridSkeletonProps = {
  settings?: Record<string, unknown>;
  height: string;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;

const Massive = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const { domain_slug } = useDomain();
  useGetUserInfoQuery();
  const access = useToolAccess(null);
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const {
    data: massiveHistory,
    isLoading: isHistoryLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useGetMassiveHistoryQuery(undefined, {
    skip: access.isLoading || !access.haveAccess
  });

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات مرتبط', href: '#' }]);
  }, [setBreadcrumbData]);

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
    {
      isError: access.isError,
      error: access.error,
      refetch: access.refetch,
      isFetching: access.isFetching,
    },
  ]);

  if (!access.isLoading && !access.haveAccess) {
    return (
      <ToolAccessDenied access={access} tool_label="ابزار کلمات مرتبط" />
    );
  }
  if (queryError) return <DataLoadError {...queryError} />;

  const isPageLoading = access.isLoading || isHistoryLoading;

  return (
    <>
      <RelatedKeywordsModeToggle mode="manual" />
      <Row className="g-3 align-items-stretch">
        <Col lg={5} xs={12}>
          {isPageLoading ? (
            <MassiveFormSkeleton />
          ) : (
            <MassiveNewForm
              massiveHistory={massiveHistory}
              isHistoryLoading={isHistoryLoading}
            />
          )}
        </Col>
        <Col lg={7} xs={12}>
          <Card className="h-100">
            <Card.Body className="p-2">
              {isPageLoading ? (
                <TypedAgGridSkeleton
                  settings={{
                    pagination: false,
                    fullscreen: false,
                    changeHeight: false,
                    resetGrid: false,
                    saveGrid: false,
                    sideBar: false,
                    exportBtn: false
                  }}
                  height="480px"
                />
              ) : (
                <div className="for-style" ref={gridRef}>
                  <TypedAgGridWrapper
                    settings={{
                      pagination: false,
                      fullscreen: false,
                      changeHeight: false,
                      resetGrid: false,
                      saveGrid: false,
                      sideBar: false,
                      exportBtn: false
                    }}
                    height="480px"
                    columns={massiveColumnConfig(domain_slug)}
                    data={massiveHistory?.data ?? null}
                  />
                  <style>{`.for-style .ag-row-focus { z-index: 2; }`}</style>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Massive;
